import { style, styleVariants, globalStyle, createVar } from '@vanilla-extract/css';
import { vars } from '../../theme';
import { reducedMotion } from '../../theme/media';

export type SwitchSize = 'sm' | 'md' | 'lg';

// distance the knob travels when toggled on — set per size on the track, read by the shared checked rule
export const knobTravel = createVar();

export const root = style({
  display: 'inline-flex',
  alignItems: 'center',
  gap: vars.spacing.gap.sm,
  cursor: 'pointer',
  userSelect: 'none',
  fontFamily: vars.typography.fontFamily.primary,
  color: vars.typography.textColor.primary,
});

export const rootDisabled = style({
  cursor: 'not-allowed',
  opacity: 0.5,
});

export const controlWrapper = style({
  position: 'relative',
  display: 'inline-flex',
  flexShrink: 0,
});

export const nativeInput = style({
  position: 'absolute',
  inset: 0,
  width: '100%',
  height: '100%',
  margin: 0,
  opacity: 0,
  cursor: 'inherit',
});

const trackBase = {
  display: 'inline-flex',
  alignItems: 'center',
  boxSizing: 'border-box' as const,
  padding: '2px',
  borderRadius: vars.spacing.borderRadius.full,
  background: vars.surface.card.default,
  border: `1px solid ${vars.surface.border.primary}`,
  boxShadow: vars.form.fieldInsetShadow,
  transition: `background ${vars.animation.duration.fast} ${vars.animation.easing.easeOut}, border-color ${vars.animation.duration.fast} ${vars.animation.easing.easeOut}`,
  '@media': { [reducedMotion]: { transition: 'none' } },
} as const;

export const track = styleVariants({
  sm: { ...trackBase, width: '32px', height: '18px', vars: { [knobTravel]: '14px' } },
  md: { ...trackBase, width: '40px', height: '22px', vars: { [knobTravel]: '18px' } },
  lg: { ...trackBase, width: '48px', height: '26px', vars: { [knobTravel]: '22px' } },
});

const knobBase = {
  boxSizing: 'border-box' as const,
  borderRadius: vars.spacing.borderRadius.full,
  background: vars.typography.textColor.primary,
  boxShadow: vars.shadow.card,
  transform: 'translateX(0)',
  transition: `transform ${vars.animation.duration.normal} ${vars.animation.easing.easeOut}`,
  '@media': { [reducedMotion]: { transition: 'none' } },
} as const;

export const knob = styleVariants({
  sm: { ...knobBase, width: '14px', height: '14px' },
  md: { ...knobBase, width: '18px', height: '18px' },
  lg: { ...knobBase, width: '22px', height: '22px' },
});

export const labelText = styleVariants({
  sm: { fontSize: vars.typography.fontSize.xs },
  md: { fontSize: vars.typography.fontSize.sm },
  lg: { fontSize: vars.typography.fontSize.base },
});

// checked → accent track + knob slides to the end (travel distance read from per-size var)
globalStyle(`${controlWrapper} input:checked + span`, {
  background: vars.bright.primary.default,
  borderColor: vars.bright.primary.default,
});
globalStyle(`${controlWrapper} input:checked + span > span`, {
  transform: `translateX(${knobTravel})`,
});

// hover (only when enabled)
globalStyle(`${controlWrapper}:hover input:not(:disabled) + span`, {
  borderColor: vars.surface.border.accent,
});

// focus-visible ring on the track
globalStyle(`${controlWrapper} input:focus-visible + span`, {
  outline: `${vars.focus.ring.width} ${vars.focus.ring.style} ${vars.focus.ring.color}`,
  outlineOffset: vars.focus.ring.offset,
});
